import type { ColorKey } from '@iheartjane/reefer';
import { Flex, Typography } from '@iheartjane/reefer';
import type {
  AriaAttributes,
  CSSProperties,
  MouseEventHandler,
  PropsWithChildren,
  ReactNode,
} from 'react';

import { useIpadMediaQuery } from '@jane/kiosk/hooks';

const baseIconStyle: CSSProperties = {
  alignItems: 'center',
  borderWidth: 0,
  boxShadow: '0px 4px 48px rgba(0, 0, 0, 0.05)',
  cursor: 'pointer',
  display: 'flex',
  justifyContent: 'center',
  textAlign: 'center',
  textDecoration: 'none',
  transition: 'all 250ms',
};

const defaultIconStyle: CSSProperties = {
  ...baseIconStyle,
  borderRadius: '64px',
  height: '80px',
  padding: '24px',
  width: '80px',
};

const tabletIconStyle: CSSProperties = {
  ...baseIconStyle,
  borderRadius: '48px',
  height: '56px',
  padding: '16px',
  width: '56px',
};

const largeIconStyle: CSSProperties = {
  ...baseIconStyle,
  borderRadius: '160px',
  height: '144px',
  padding: '48px',
  width: '144px',
};

const tabletLargeIconStyle: CSSProperties = {
  ...baseIconStyle,
  borderRadius: '56px',
  height: '104px',
  padding: '32px',
  width: '104px',
};

interface IconButtonSpacing {
  margin?: CSSProperties['margin'];
  marginBottom?: CSSProperties['marginBottom'];
  marginLeft?: CSSProperties['marginLeft'];
  marginRight?: CSSProperties['marginRight'];
  marginTop?: CSSProperties['marginTop'];
  padding?: CSSProperties['padding'];
}

interface InnerIconButtonProps {
  ariaLabel?: AriaAttributes['aria-label'];
  backgroundColor?: CSSProperties['backgroundColor'];
  className?: string;
  disabled?: boolean;
  disabledOpacity?: number;
  icon: ReactNode;
  large?: boolean;
  onClick?: MouseEventHandler;
  spacing?: IconButtonSpacing;
  style?: CSSProperties;
}

export type IconButtonProps = PropsWithChildren<InnerIconButtonProps>;

export const IconButton = ({
  ariaLabel,
  backgroundColor = 'white',
  children,
  className,
  disabled = false,
  disabledOpacity = 0.3,
  icon,
  large = false,
  onClick,
  spacing,
  style,
}: IconButtonProps) => {
  const isIpad = useIpadMediaQuery();
  let iconStyle = isIpad ? tabletIconStyle : defaultIconStyle;
  if (large) {
    iconStyle = isIpad ? tabletLargeIconStyle : largeIconStyle;
  }

  return (
    <button
      className={className}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}
      style={{
        backgroundColor,
        position: 'relative',
        ...(disabled && { opacity: disabledOpacity, pointerEvents: 'none' }),
        ...iconStyle,
        ...spacing,
        ...style,
      }}
    >
      {icon}
      {children}
    </button>
  );
};

export interface LabeledIconButtonProps extends IconButtonProps {
  labelColor?: ColorKey;
  selected?: boolean;
  title: string;
}

export const LabeledIconButton = ({
  labelColor,
  selected = false,
  title,
  ...rest
}: LabeledIconButtonProps) => {
  return (
    <Flex flexDirection="column" alignItems="center">
      <IconButton {...rest} />
      <Typography
        as="span"
        color={labelColor}
        mt={24}
        textAlign="center"
        variant={selected ? 'h4-semibold' : 'h4-regular'}
        whiteSpace="nowrap"
      >
        {title}
      </Typography>
    </Flex>
  );
};
